import { useMutation, useQueryClient } from 'react-query'

import { api } from '../../../api'
import { type ExpertDomain } from '../../../../types/domain/expert-domain'

export type UpdateExpertParams = {
  name: string
  personType?: string
  email: string
  phone: string
  cpf?: string | null
  bio?: string
  observations?: string
  linkedin?: string
  researcherId?: number
  researcherAcronym?: string
  agreeParticipateMeet?: boolean | string
  doNotContactTemporarily?: boolean | string
  lastContactAt?: string
  showNotification?: boolean
  nationality?: string
  bankCountry?: string
  meetingLanguages?: string[]
  otherLanguage?: string
  naturalData?: {
    birthdate: string
    rg: string
    cep: string
    street: string
    city: string
    state: string
    neighborhood: string
    addressNumber: string
    complement: string
  }
  legalData?: {
    companyName: string
    cnpj: string
  }
  bankData?:
  | {
    bank: string
    bankBranch: string
    currentAccount: string
    pix: string
  }
  | {
    swiftCode?: string
    bankAccountNumber?: string
    internationalBankBranch?: string
    amazonGiftCardEmail?: string
  }
}

const updateExpert = async (
  id: number,
  data: Partial<UpdateExpertParams>
): Promise<ExpertDomain> => {
  if (
    typeof data.agreeParticipateMeet === 'number' &&
    typeof data.doNotContactTemporarily === 'number'
  ) {
    data.agreeParticipateMeet = data.agreeParticipateMeet === 1
    data.doNotContactTemporarily = data.doNotContactTemporarily === 1
  }

  const agreeParticipateMeet: boolean =
    (typeof data.agreeParticipateMeet !== 'boolean' &&
    data.agreeParticipateMeet !== null
      ? ['true', 'yes'].includes(data.agreeParticipateMeet ?? 'false')
      : data.agreeParticipateMeet) ?? undefined

  const doNotContactTemporarily =
    (typeof data.doNotContactTemporarily !== 'boolean' &&
    data.doNotContactTemporarily !== null
      ? ['true', 'yes'].includes(data.doNotContactTemporarily ?? 'false')
      : data.doNotContactTemporarily) ?? undefined

  const response = await api.put<ExpertDomain>(`/experts/${id}`, {
    ...data,
    agreeParticipateMeet,
    doNotContactTemporarily,
  })

  return response.data
}

export const useExpertMutation = (id: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn ({
      id,
      data,
    }: {
      id: number
      data: Partial<UpdateExpertParams>
    }) {
      return updateExpert(id, data)
    },
    onSuccess (data) {
      queryClient.setQueryData(['expert', data.id], data)
    },
  })
}
